import {
  useCallback,
  useState,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from "react";
import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { TOptions } from "i18next";
import type { ResultAsync } from "neverthrow";
import { err, fromPromise, ok } from "neverthrow";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import { z } from "zod";

import type { InternalError } from "@thalamos/common";
import { buildInternalError } from "@thalamos/common";
import type { IncidentWorkItemWithContext } from "@thalamos/vision-common";
import {
  buildTranslationInput,
  getLatestPlaceOfSafety,
  getSupportedIncidentTypeSelection,
  getWorkflowTranslationContext,
  IncidentWorkItemIdSchema,
  tk,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { Banner } from "../../components/Banner/Banner.js";
import { Footer } from "../../components/FormBuilder/MultiPageForm/Footer.js";
import { Header } from "../../components/FormBuilder/MultiPageForm/Header.js";
import { TypographyI18N } from "../../components/index.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { FormWrapper } from "../../layout/FormWrapper.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";
import { PdfPreviewPage } from "./PdfPreviewPage.js";
import type { OptionsModelProps } from "./ShareOptionsPage.js";
import { SHARE_OPTIONS, ShareOptionsPage } from "./ShareOptionsPage.js";
import {
  SharePageWithEmail,
  SharePageWithoutEmail,
  UnableToShareWithPos,
} from "./SharePage.js";

export function ShareFlow() {
  const navigate = useNavigate();
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    {
      data: incidentWorkItem,
      error: incidentWorkItemError,
      loading: incidentWorkItemLoading,
    },
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const [{ data: pdfData, error: pdfError, loading: pdfLoading }] =
    apiHooks.incidentWorkItem.getPdf(incidentWorkItemId.data);

  const onCancel = useCallback(() => {
    if (incidentWorkItem !== undefined) {
      navigate(
        createRoute.incidentProgressPage(incidentWorkItem.incidentWorkItem.id),
      );
    }
  }, [incidentWorkItem, navigate]);

  const onClose = useCallback(() => {
    if (incidentWorkItem !== undefined) {
      navigate(
        createRoute.incidentProgressPage(incidentWorkItem.incidentWorkItem.id),
      );
    }
  }, [incidentWorkItem, navigate]);

  const onUnableToShareComplete = useCallback(() => {
    if (incidentWorkItem !== undefined) {
      const target = match(
        getSupportedIncidentTypeSelection({
          incidentWorkItem,
        })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () =>
          createRoute.incidentFinishFormPage(
            incidentWorkItem.incidentWorkItem.id,
          ),
        )
        .with("136", () =>
          createRoute.incidentPreEndIncidentPage(
            incidentWorkItem.incidentWorkItem.id,
          ),
        )
        .with(undefined, () =>
          createRoute.incidentPreEndIncidentPage(
            incidentWorkItem.incidentWorkItem.id,
          ),
        )
        .exhaustive();

      navigate(target);
    }
  }, [incidentWorkItem, navigate]);

  const onShare = useCallback(
    (emails?: string[]): ResultAsync<void, InternalError> =>
      fromPromise(
        api.incidentWorkItem.share(incidentWorkItemId.data, emails),
        (error) =>
          buildInternalError({
            message: "Failed to share incident",
            issues: [error instanceof Error ? error.message : String(error)],
          }),
      ).andThen((res) => {
        const data: unknown = res.data;
        if (
          res.status === 200 &&
          typeof data === "object" &&
          data !== null &&
          "outcome" in data &&
          data.outcome === "success"
        ) {
          return ok(undefined);
        }
        const reason =
          typeof data === "object" &&
          data !== null &&
          "reason" in data &&
          typeof data.reason === "string"
            ? data.reason
            : "Unexpected error sharing incident";
        return err(
          buildInternalError({
            message: reason,
          }),
        );
      }),
    [incidentWorkItemId.data],
  );

  if (incidentWorkItemError) {
    return <ErrorComponent />;
  }

  if (incidentWorkItemLoading || !incidentWorkItem) {
    return <CircularProgress />;
  }

  const pdfUnavailable = !!pdfError || (!pdfLoading && !pdfData);

  return (
    <ShareFlowInner
      pdfData={pdfData ?? null}
      pdfLoading={pdfLoading}
      pdfUnavailable={pdfUnavailable}
      incidentWorkItem={incidentWorkItem}
      onCancel={onCancel}
      onClose={onClose}
      onUnableToShareComplete={onUnableToShareComplete}
      onShare={onShare}
    />
  );
}

export function ShareFlowInner({
  pdfData,
  pdfLoading,
  pdfUnavailable,
  incidentWorkItem,
  onCancel,
  onClose,
  onUnableToShareComplete,
  onShare,
}: {
  pdfData: string | null;
  pdfLoading: boolean;
  pdfUnavailable: boolean;
  incidentWorkItem: IncidentWorkItemWithContext;
  onCancel: () => void;
  onClose: () => void;
  onUnableToShareComplete: () => void;
  onShare: (emails?: string[]) => ResultAsync<void, InternalError>;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = 3;
  const theme = useTheme();

  const { t } = useTranslation();
  const notifications = useToastNotifications();

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [checkboxError, setCheckboxError] = useState<string | undefined>(
    undefined,
  );
  const [isCheckboxFormReviewChecked, setIsCheckboxFormReviewChecked] =
    useState(false);
  const [formReviewCheckboxError, setFormReviewCheckboxError] = useState<
    string | undefined
  >(undefined);

  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedShareOption, setSelectedShareOption] =
    useState<OptionsModelProps | null>(null);
  const [maybeEmail, setMaybeEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const latestPlaceOfSafety = getLatestPlaceOfSafety(incidentWorkItem);
  const latestPlaceOfSafetyName = latestPlaceOfSafety?.name;
  const latestPlaceOfSafetyAssociatedEmails =
    latestPlaceOfSafety?.associatedEmails;

  const [emails, setEmails] = useState<string[]>(
    latestPlaceOfSafetyAssociatedEmails ?? [],
  );

  const tOptions = {
    context: getWorkflowTranslationContext({
      incidentWorkItem,
    }),
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(event.target.checked);
    if (event.target.checked) {
      setCheckboxError(undefined);
    }
  };

  const handleShare = useCallback(async () => {
    if (selectedShareOption === SHARE_OPTIONS.EMAIL) {
      if (emails.length === 0) {
        notifications.show("Please enter a valid email address.", {
          severity: "warning",
        });
        return;
      }
      const parsedEmail = z
        .string()
        .trim()
        .pipe(z.email())
        .safeParse(maybeEmail);
      if (parsedEmail.success) {
        setError(t("sharePage.warningAddEmail"));
        return;
      }
    }

    setSubmitting(true);
    const shareEmail =
      selectedShareOption === SHARE_OPTIONS.EMAIL ? emails : undefined;
    const result = await onShare(shareEmail);
    setSubmitting(false);
    result.match(
      () => {
        notifications.show("Incident shared successfully", {
          severity: "success",
        });
        setShowSuccess(true);
      },
      (shareError) => {
        notifications.show(shareError.message, { severity: "error" });
      },
    );
  }, [selectedShareOption, emails, maybeEmail, notifications, t, onShare]);

  // Function to determine if the user can proceed to the next page
  function getCanProceed(): boolean {
    if (currentPage === 0) {
      return !pdfLoading;
    }

    if (currentPage === 1) {
      return false;
    }

    if (currentPage === 2) {
      if (selectedShareOption === SHARE_OPTIONS.EMAIL) {
        return emails.length !== 0;
      }

      if (selectedShareOption === SHARE_OPTIONS.WITHOUT_EMAIL) {
        return true;
      }

      if (selectedShareOption === SHARE_OPTIONS.UNABLE) {
        return true;
      }
    }

    return false;
  }

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {showSuccess ? (
        <ShareSuccess
          emails={emails}
          onClose={onClose}
          showEmailSuccess={
            !!(selectedShareOption === SHARE_OPTIONS.EMAIL && emails)
          }
          activePlaceOfSafetyName={latestPlaceOfSafetyName}
          translationOptions={tOptions}
        />
      ) : (
        <>
          <FormWrapper>
            <Header
              title={buildTranslationInput(tk("sharePage.title"))}
              page={currentPage}
              totalPages={totalPages}
              onBack={() => {
                if (currentPage > 0) setCurrentPage((prev) => prev - 1);
              }}
            />

            {/* Page content rendering */}
            {currentPage === 0 && (
              <PdfPreviewPage
                pdfData={pdfData}
                pdfLoading={pdfLoading}
                pdfUnavailable={pdfUnavailable}
              />
            )}
            {currentPage === 1 && (
              <ShareOptionsPage
                placeName={latestPlaceOfSafetyName ?? "Unknown"}
                onSelectOption={(option) => {
                  setSelectedShareOption(option);
                  setCurrentPage(2);
                }}
                incidentWorkItem={incidentWorkItem}
              />
            )}
            {currentPage === 2 &&
              selectedShareOption === SHARE_OPTIONS.EMAIL && (
                <SharePageWithEmail
                  emails={emails}
                  setEmails={setEmails}
                  submitting={submitting}
                  activePlaceOfSafetyName={latestPlaceOfSafetyName}
                  hasPosEmails={
                    (latestPlaceOfSafetyAssociatedEmails ?? []).length > 0
                  }
                  setMaybeEmail={setMaybeEmail}
                  maybeEmail={maybeEmail}
                  setError={setError}
                  error={error}
                  incidentWorkItem={incidentWorkItem}
                />
              )}

            {currentPage === 2 &&
              selectedShareOption === SHARE_OPTIONS.WITHOUT_EMAIL && (
                <SharePageWithoutEmail
                  aspireFrontendUrl={
                    incidentWorkItem.incidentWorkItem.aspireFrontendUrl
                  }
                  activePlaceOfSafetyName={latestPlaceOfSafetyName}
                  shareCode={incidentWorkItem.incidentWorkItem.shareCode}
                  checkboxChecked={isCheckboxFormReviewChecked}
                  onCheckboxChange={(e) => {
                    setIsCheckboxFormReviewChecked(e.target.checked);
                    if (e.target.checked) {
                      setFormReviewCheckboxError(undefined);
                    }
                  }}
                  checkboxError={formReviewCheckboxError}
                  incidentWorkItem={incidentWorkItem}
                />
              )}

            {/* Warning banner */}
            {currentPage === 2 &&
              selectedShareOption === SHARE_OPTIONS.UNABLE && (
                <UnableToShareWithPos translationOptions={tOptions} />
              )}
            {(isDesktop || currentPage !== 0) && (
              <ShareFooter
                isCheckboxChecked={isCheckboxChecked}
                handleCheckboxChange={handleCheckboxChange}
                checkboxError={checkboxError}
                setCheckboxError={setCheckboxError}
                isCheckboxFormReviewChecked={isCheckboxFormReviewChecked}
                formReviewCheckboxError={formReviewCheckboxError}
                setFormReviewCheckboxError={setFormReviewCheckboxError}
                currentPage={currentPage}
                totalPages={totalPages}
                selectedShareOption={selectedShareOption}
                handleShare={handleShare}
                getCanProceed={getCanProceed}
                setCurrentPage={setCurrentPage}
                onCancel={onCancel}
                onUnableToShareComplete={onUnableToShareComplete}
                pdfUnavailable={pdfUnavailable}
              />
            )}
          </FormWrapper>
          {currentPage === 0 && !isDesktop && (
            <ShareFooter
              isCheckboxChecked={isCheckboxChecked}
              handleCheckboxChange={handleCheckboxChange}
              checkboxError={checkboxError}
              setCheckboxError={setCheckboxError}
              isCheckboxFormReviewChecked={isCheckboxFormReviewChecked}
              formReviewCheckboxError={formReviewCheckboxError}
              setFormReviewCheckboxError={setFormReviewCheckboxError}
              currentPage={currentPage}
              totalPages={totalPages}
              selectedShareOption={selectedShareOption}
              handleShare={handleShare}
              getCanProceed={getCanProceed}
              setCurrentPage={setCurrentPage}
              onCancel={onCancel}
              onUnableToShareComplete={onUnableToShareComplete}
              pdfUnavailable={pdfUnavailable}
            />
          )}
        </>
      )}
    </>
  );
}

type ShareFooterProps = {
  isCheckboxChecked: boolean;
  handleCheckboxChange: (e: ChangeEvent<HTMLInputElement>) => void;
  checkboxError: string | undefined;
  setCheckboxError: Dispatch<SetStateAction<string | undefined>>;
  isCheckboxFormReviewChecked: boolean;
  formReviewCheckboxError: string | undefined;
  setFormReviewCheckboxError: Dispatch<SetStateAction<string | undefined>>;
  currentPage: number;
  totalPages: number;
  selectedShareOption: OptionsModelProps | null;
  handleShare: () => Promise<void>;
  getCanProceed: () => boolean;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  onCancel: () => void;
  onUnableToShareComplete: () => void;
  pdfUnavailable: boolean;
};

export const ShareFooter = ({
  isCheckboxChecked,
  handleCheckboxChange,
  checkboxError,
  setCheckboxError,
  isCheckboxFormReviewChecked,
  formReviewCheckboxError: _formReviewCheckboxError,
  setFormReviewCheckboxError,
  currentPage,
  totalPages,
  selectedShareOption,
  handleShare,
  getCanProceed,
  setCurrentPage,
  onCancel,
  onUnableToShareComplete,
  pdfUnavailable,
}: ShareFooterProps) => {
  const { t } = useTranslation();
  const notifications = useToastNotifications();

  // Footer label based on selected share option
  const footerLabel =
    currentPage === 0 || currentPage === 1
      ? tk("multiPageForm.next")
      : selectedShareOption === SHARE_OPTIONS.UNABLE
        ? tk("sharePage.unableToShare.endForm")
        : selectedShareOption === SHARE_OPTIONS.WITHOUT_EMAIL
          ? tk("common.finish")
          : tk("multiPageForm.next");

  return (
    <>
      {/* Footer navigation */}
      <Box
        sx={{
          position: "sticky",
          bottom: "0rem",
          zIndex: 999,
          backgroundColor: "white",
          padding: "1rem 1rem",
        }}
      >
        <Box sx={{ mb: 2 }}>
          {currentPage === 0 && !pdfUnavailable && (
            <FormControl error={!!checkboxError}>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="checkbox-form-review"
                    checked={isCheckboxChecked}
                    onChange={handleCheckboxChange}
                    name="confirmCheckbox"
                    color={checkboxError ? "error" : "primary"}
                  />
                }
                label={t("sharePage.confirmCheckboxLabel")}
              />
              {checkboxError && (
                <FormHelperText>{checkboxError}</FormHelperText>
              )}
            </FormControl>
          )}
        </Box>

        <Footer
          onCancel={{
            handler: onCancel,
          }}
          onNext={
            getCanProceed()
              ? {
                  handler: async () => {
                    if (
                      currentPage === 0 &&
                      !pdfUnavailable &&
                      !isCheckboxChecked
                    ) {
                      setCheckboxError(t("sharePage.checkboxErrorMessage"));
                      return;
                    }
                    if (currentPage < totalPages - 1) {
                      setCurrentPage((prev) => prev + 1);
                    } else {
                      if (selectedShareOption === SHARE_OPTIONS.EMAIL) {
                        await handleShare();
                      } else if (selectedShareOption === SHARE_OPTIONS.UNABLE) {
                        onUnableToShareComplete();
                      } else if (
                        selectedShareOption === SHARE_OPTIONS.WITHOUT_EMAIL
                      ) {
                        if (!isCheckboxFormReviewChecked) {
                          setFormReviewCheckboxError(
                            t(
                              "sharePage.shareWithoutEmail.checkboxErrorMessage",
                            ),
                          );
                          return;
                        }
                        await handleShare();
                      } else {
                        notifications.show(
                          "Please select 'Share by Email' to proceed with sharing.",
                          { severity: "warning" },
                        );
                      }
                    }
                  },
                  label: footerLabel,
                }
              : {
                  handler: undefined,
                  label: footerLabel,
                }
          }
        />
      </Box>
    </>
  );
};

const ShareSuccess = ({
  emails,
  onClose,
  showEmailSuccess,
  activePlaceOfSafetyName,
  translationOptions,
}: {
  emails: string[];
  onClose: () => void;
  showEmailSuccess: boolean;
  activePlaceOfSafetyName?: string;
  translationOptions: TOptions;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box data-testid="share-success">
      <FormWrapper>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            margin: "2rem 0",
            justifyContent: "space-between",
            minHeight: "100%",
          }}
        >
          <Box sx={{ display: "flex" }}>
            <CheckCircle color="success" />
            <TypographyI18N
              translationKey={tk("sharePage.successPage.title")}
              sx={{
                marginLeft: "1rem",
                fontWeight: theme.typography.fontWeightMedium,
              }}
            />
          </Box>
          <Banner
            body={{
              translationKey: tk("sharePage.successPage.warningBannerMessage"),
              translationOptions,
            }}
            bannerType={"warning"}
          />
          {showEmailSuccess ? (
            <>
              <Typography
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <Trans
                  i18nKey="sharePage.successPage.successMessage"
                  values={{ email: emails.join(", ") }}
                  components={{ bold: <strong /> }}
                />
              </Typography>
              <TypographyI18N
                translationKey={tk("sharePage.successPage.accessForm")}
              />
              <TypographyI18N
                translationKey={tk("sharePage.successPage.verificationLink")}
                sx={{ pl: 2 }}
              />
              <TypographyI18N
                translationKey={tk("sharePage.successPage.confirmedEmail")}
                sx={{ pl: 2 }}
              />
              <TypographyI18N
                translationKey={tk("sharePage.successPage.guestAccessCode")}
                sx={{ pl: 2 }}
              />
              <TypographyI18N
                translationKey={tk(
                  "sharePage.successPage.awaitHealthServicesNotification",
                )}
                translationOptions={translationOptions}
              />
            </>
          ) : (
            <>
              <Typography>
                <Trans
                  i18nKey="sharePage.successPage.handoverInitiated"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any -- Need to figure out how to plumb the correct types into all of these <Trans> components
                  tOptions={translationOptions as any}
                  values={{ posName: activePlaceOfSafetyName }}
                  components={{ bold: <strong /> }}
                />
              </Typography>

              <TypographyI18N
                translationKey={tk("sharePage.successPage.formSharedUrl")}
              />
              <TypographyI18N
                translationKey={tk("sharePage.successPage.clickCloseMessage")}
                translationOptions={translationOptions}
              />
            </>
          )}
        </Stack>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "white",
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              data-testid="close-button"
              color="error"
              sx={{ textDecoration: "underline" }}
              onClick={onClose}
            >
              {t("common.close")}
            </Button>
          </Box>
        </Box>
      </FormWrapper>
    </Box>
  );
};
